import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SharedTable from '../../../../../shared/Table/SharedTable';
import StatusBadge from '../../../../../shared/StatusBadge/StatusBadge';
import UnifiedDateRangePicker from './UnifiedDateRangePicker';
import { formatAssignmentDateSpan } from '../../../../../../utils/dateUtils';
import '../../../../../../components/shared/filterStyles.css';

const STATUS_OPTIONS = [
    { value: 'aktiv', label: 'Aktiv', color: '#0284c7' },
    { value: 'pausiert', label: 'Pausiert', color: '#f59e0b' },
    { value: 'abgeschlossen', label: 'Abgeschlossen', color: '#10b981' },
    { value: 'undatiert', label: 'Undatiert', color: '#94a3b8' }
];

/**
 * JSDoc for AssignmentsDetailView component.
 * 
 * @component
 * @param {Object} props - Component properties.
 * @param {Array} props.data - List of leader assignments.
 * @param {boolean} props.isLoading - Loading state indicator.
 * @returns {React.ReactElement} The assignments details view.
 */
export default function AssignmentsDetailView({ data = [], isLoading = false, project }) {
    const navigate = useNavigate();
    const [searchText, setSearchText] = useState('');
    const [statusFilter, setStatusFilter] = useState(['aktiv', 'pausiert', 'abgeschlossen', 'undatiert']);
    const [dateRange, setDateRange] = useState({ start: '', end: '' });
    const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);

    const hasActiveFilters = !!(
        searchText || 
        statusFilter.length !== STATUS_OPTIONS.length || 
        dateRange.start || 
        dateRange.end
    );

    // Close status dropdown on click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsStatusDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const filteredData = useMemo(() => {
        return data.filter(item => {
            // Text search
            let matchesText = true;
            if (searchText) {
                const searchLower = searchText.toLowerCase();
                matchesText = (
                    (item.title && item.title.toLowerCase().includes(searchLower)) ||
                    (item.notes && item.notes.toLowerCase().includes(searchLower)) ||
                    (item.location && item.location.toLowerCase().includes(searchLower))
                );
            }

            // Status filter (multiselect array check)
            const itemStatus = (item.status || '').toLowerCase();
            const matchesStatus = statusFilter.includes(itemStatus);

            // Date filter
            let matchesDate = true;
            if (dateRange.start || dateRange.end) {
                if (!item.hint_date_start) {
                    matchesDate = false;
                } else {
                    const itemStart = new Date(item.hint_date_start);
                    itemStart.setHours(0, 0, 0, 0);
                    const itemEnd = item.hint_date_end ? new Date(item.hint_date_end) : itemStart;
                    itemEnd.setHours(23, 59, 59, 999);

                    if (dateRange.start) {
                        const start = new Date(dateRange.start);
                        start.setHours(0, 0, 0, 0);
                        matchesDate = matchesDate && itemEnd >= start;
                    }
                    if (dateRange.end) {
                        const end = new Date(dateRange.end);
                        end.setHours(23, 59, 59, 999);
                        matchesDate = matchesDate && itemStart <= end;
                    }
                }
            }

            return matchesText && matchesStatus && matchesDate;
        });
    }, [data, searchText, statusFilter, dateRange]);

    const handleReset = () => {
        setSearchText('');
        setStatusFilter(['aktiv', 'pausiert', 'abgeschlossen', 'undatiert']);
        setDateRange({ start: '', end: '' });
    };

    const toggleStatusFilter = (val) => {
        if (statusFilter.includes(val)) {
            setStatusFilter(statusFilter.filter(item => item !== val));
        } else {
            setStatusFilter([...statusFilter, val]);
        }
    };

    const getStatusLabel = () => {
        if (statusFilter.length === 0) return 'Keine';
        if (statusFilter.length === STATUS_OPTIONS.length) return 'Alle';
        if (statusFilter.length === 1) {
            return STATUS_OPTIONS.find(o => o.value === statusFilter[0])?.label || '';
        }
        return `${statusFilter.length} angewählt`;
    };

    const columns = [
        {
            key: 'hint_date_start',
            label: 'Zeitraum',
            sortable: true,
            render: (la) => (
                <span>
                    {formatAssignmentDateSpan({
                        start_datetime: la.hint_date_start,
                        end_datetime: la.hint_date_end
                    })}
                </span>
            ),
            className: 'date-cell'
        },
        {
            key: 'title',
            label: 'Titel',
            sortable: true,
            render: (la) => la.title || 'Ohne Titel',
            className: 'bold-cell'
        },
        {
            key: 'location',
            label: 'Ort',
            sortable: true,
            render: (la) => la.location || <span className="text-muted">-</span>
        },
        {
            key: 'notes',
            label: 'Notizen',
            sortable: true,
            render: (la) => la.notes || <span className="text-muted">-</span>,
            className: 'notes-cell'
        },
        {
            key: 'status',
            label: 'Status',
            sortable: true,
            render: (la) => <StatusBadge status={la.status} />,
            className: 'align-center'
        }
    ];

    const handleRowClick = (item) => {
        if (project) {
            sessionStorage.setItem('projectModalBackup', JSON.stringify({
                isOpen: true,
                activeTab: 'analysis',
                currentMode: 'view',
                project: project
            }));
            sessionStorage.setItem('projectAnalysis_activeSubView', 'assignments');
        }
        navigate(`/tools/leader`, {
            state: {
                openAssignmentId: item.id
            }
        });
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="co-filters-panel">
                {/* Search field */}
                <div className="co-filter-group">
                    <label>Suche</label>
                    <input
                        type="text"
                        className="co-filter-input"
                        placeholder="Nach Titel, Ort oder Notizen filtern..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                    />
                </div>

                {/* Multiselect Status Dropdown */}
                <div className="co-filter-group" ref={dropdownRef}>
                    <label>Status</label>
                    <button
                        type="button"
                        className="co-filter-dropdown"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            width: '100%',
                            cursor: 'pointer',
                            textAlign: 'left'
                        }}
                        onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
                    >
                        <span>{getStatusLabel()}</span>
                        <span style={{ fontSize: '0.75rem', color: '#64748b' }}>▼</span>
                    </button>
                    {isStatusDropdownOpen && (
                        <div className="co-filter-dropdown-menu">
                            {STATUS_OPTIONS.map(opt => {
                                const isChecked = statusFilter.includes(opt.value);
                                return (
                                    <div
                                        key={opt.value}
                                        className="co-filter-item"
                                        onClick={() => toggleStatusFilter(opt.value)}
                                    >
                                        <div className={`co-custom-checkbox ${isChecked ? 'checked' : ''}`}>
                                            {isChecked && <span className="checkmark">✓</span>}
                                        </div>
                                        <span
                                            className="co-status-dot-mini"
                                            style={{ backgroundColor: opt.color }}
                                        />
                                        <span style={{ fontSize: '0.85rem', fontWeight: 500 }}>{opt.label}</span>
                                    </div>
                                );
                            })}
                            <div className="co-filter-actions-row">
                                <span
                                    className="co-filter-action-btn"
                                    onClick={() => setStatusFilter(STATUS_OPTIONS.map(o => o.value))}
                                >
                                    Alle
                                </span>
                                <span style={{ color: '#e2e8f0' }}>|</span>
                                <span
                                    className="co-filter-action-btn"
                                    onClick={() => setStatusFilter([])}
                                >
                                    Keine
                                </span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Datepicker */}
                <UnifiedDateRangePicker
                    startDate={dateRange.start}
                    endDate={dateRange.end}
                    onChange={setDateRange}
                />

                {/* Reset button */}
                <button
                    type="button"
                    className={`co-filter-reset-btn ${!hasActiveFilters ? 'disabled' : ''}`}
                    disabled={!hasActiveFilters}
                    onClick={handleReset}
                    title="Filter zurücksetzen"
                >
                    🔄
                </button>
            </div>

            <div className="subview-content-list glass-card">
                <SharedTable
                    data={filteredData}
                    columns={columns}
                    uniqueKeyField="id"
                    onRowClick={handleRowClick}
                    isLoading={isLoading}
                    emptyMessage="Keine Einsätze für die gewählten Filter gefunden."
                />
            </div>
        </div>
    );
}
